// src/lib/tiers.js
//
// THE ONE PLACE THAT KNOWS WHAT YOU ARE LOOKING AT. #417, decisions D1, D2 and D3,
// approved by Colby on Sep 14, 2026. Built at Session 79.15b.
//
// NOTHING IMPORTS THIS YET AND THAT IS DELIBERATE. The 44 existing width
// comparisons are converted page by page from Session 79.15c onward, each one
// checked on screen. Adding the module and converting the app are separate jobs
// on purpose — #532's own lesson is that a boundary stated in many places drifts,
// and the cure for that is not a second hurried sweep.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS EXISTS, in one paragraph, because the numbers are the argument.
//
// Stede has exactly ONE screen-size boundary today: 768, written 44 times across
// 28 files. 24 sites ask `<= 768` and 20 ask `< 768`, so AT EXACTLY 768 THE APP
// RENDERS TWO WAYS — the Requests inbox is desktop while Request materials is
// mobile, inside one navigation flow. Above 768 there is nothing at all, so every
// iPad except the Mini gets the DESKTOP layout driven by a finger, with 32px
// buttons. That is #532, and nothing in this repository could have found it: all
// 44 comparisons are individually correct.
// ─────────────────────────────────────────────────────────────────────────────

import { createContext, useContext, useEffect, useState } from 'react';

// D1 — FOUR TIERS, and four is the smallest set that gives the tablet a layout of
// its own without inventing a tier nobody will maintain. Material ships five and
// SAP ships five; Stede shipped one.
//
//   600  Material's own measured boundary — 99.96% of phones in portrait are below it.
//   1024 where every iPad in landscape, and the 13" in portrait, arrives.
//   1600 where SAP stops growing and starts centring. It exists purely to stop a
//        table stretching, and it is the only tier with that job.
//
// A TIER OWNS ITS LOWER BOUND — D3. Every comparison in this module is `>=`
// upward, which is what removes the `<` versus `<=` disagreement rather than
// merely picking a side of it.
export const TIER_MIN = Object.freeze({
  phone: 0,
  tablet: 600,
  desk: 1024,
  wide: 1600,
});

// Ordered widest-last. Order is load-bearing: `tierFor` walks it downward and
// `atLeast` compares by index, so a tier added in the wrong position would break
// both silently.
export const TIER_ORDER = Object.freeze(['phone', 'tablet', 'desk', 'wide']);

// TABLET INHERITS THE PHONE LAYOUT'S STRUCTURE — D1 as NARROWED by Colby at
// approval, on the business fact only he had. Asked whether the tablets are used
// at a station or carried, he said "both, so draw for the carried case".
//
// So a tablet gets one column, cards and sheets — and spends its extra room on
// bigger targets, more legible type and more rows, NOT on a second pane. A
// carried tablet is held standing, often one-handed and sometimes gloved, so
// density is the wrong thing to spend the room on.
//
// This predicate is the cheap way for a screen to ask that question without
// re-deriving the reasoning. It is NOT `tier === 'phone'`, and writing it that
// way is how the tablet quietly loses its layout again.
export function usesPhoneLayout(tier) {
  return tier === 'phone' || tier === 'tablet';
}

// Pure, so it is testable without a window. Exported for tests and for any
// caller that already has a width in hand.
export function tierFor(width) {
  const w = Number(width);
  if (!Number.isFinite(w)) return 'desk';   // unknown width behaves as the old default
  for (let i = TIER_ORDER.length - 1; i >= 0; i--) {
    if (w >= TIER_MIN[TIER_ORDER[i]]) return TIER_ORDER[i];
  }
  return 'phone';
}

// `atLeast` is the ONLY comparison form this module offers, and that is the whole
// point of D3. A caller cannot write `<` or `<=` wrongly because it is never
// given the chance to write either.
export function tierAtLeast(tier, floor) {
  return TIER_ORDER.indexOf(tier) >= TIER_ORDER.indexOf(floor);
}

// D2 — THE SECOND AXIS, AND IT IS THE DECISION THAT SOLVES THE IPAD.
//
// WIDTH DECIDES LAYOUT. POINTER DECIDES CONTROL SIZE.
//
// An iPad in landscape is 1180px — Desk by width — and has no mouse. Sizing
// controls from width alone hands it 32px buttons, which is today's defect one
// tier further out. The web platform answers this directly, and no mid-market
// competitor combines the two axes: Material gives the width classes, Apple gives
// the pointer axis, and putting them together is what the charter asked for.
//
// Read through a function rather than inline so a test can reason about a machine
// that has no matchMedia at all — jsdom does not, and neither do some older
// embedded browsers.
function readPointer() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // NOT a guess at the truth — a statement that we do not know. `coarse: false`
    // would silently hand a touch device the 32px controls, which is the exact
    // defect this axis exists to prevent, so the unknown case takes the SAFER
    // side: assume a finger, and assume no hover.
    return { coarse: true, hover: false };
  }
  return {
    coarse: window.matchMedia('(pointer: coarse)').matches,
    hover: window.matchMedia('(hover: hover)').matches,
  };
}

function readWidth() {
  return typeof window === 'undefined' ? TIER_MIN.desk : window.innerWidth;
}

// D3 — ONE HOOK, and it closes a bug class rather than only tidying a number.
//
// TEN FILES TODAY READ THE WIDTH AT RENDER TIME WITH NO RESIZE LISTENER, so they
// are stranded in the wrong layout after a rotation until something else happens
// to re-render them. A plain `window.innerWidth` read is correct exactly once, on
// mount, and silently wrong for the rest of the session. That was #64, and #164
// found the same gap a second time.
//
// ── A COLUMN NARROWER THAN ITS WINDOW -- gate 675 F4, Session 79.22b.5.5 ─────
//
// On a desk, Transfers puts its cards in a 400px column beside the chosen
// transfer. The gate's rule: "cards in the 400px desk column lay out by the
// column's width, not the window's, so a card's buttons stack as they do on a
// tablet." Every card asks useTier() for its layout, so the column says once
// which tier it is, and everything inside it lays out to that tier.
//
// ONLY THE LAYOUT TIER IS OVERRIDDEN. The pointer still comes from the device:
// a mouse in a narrow column keeps the mouse's control sizes (D2: width decides
// layout, pointer decides control size). `width` stays the window's.
// Null (the default) changes nothing anywhere.
export const LayoutTierContext = createContext(null);

// Returns { tier, width, coarse, hover, atLeast, phoneLayout }.
export function useTier() {
  const forcedTier = useContext(LayoutTierContext);
  const [state, setState] = useState(() => ({
    width: readWidth(),
    ...readPointer(),
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function sync() {
      setState(prev => {
        const next = { width: readWidth(), ...readPointer() };
        // Re-rendering every consumer on every resize frame is how a drag turns
        // into a stutter. Only publish when something a caller can act on has
        // actually changed.
        if (tierFor(prev.width) === tierFor(next.width)
            && prev.coarse === next.coarse
            && prev.hover === next.hover) {
          return prev;
        }
        return next;
      });
    }

    window.addEventListener('resize', sync);

    // THE POINTER CAN CHANGE WITHOUT A RESIZE, which is the case a width-only
    // listener misses: a Surface detaching its keyboard, or an iPad having a
    // mouse paired mid-session. Both fire the media query and neither resizes.
    const queries = typeof window.matchMedia === 'function'
      ? [window.matchMedia('(pointer: coarse)'), window.matchMedia('(hover: hover)')]
      : [];
    // addEventListener on a MediaQueryList is the modern form; addListener is the
    // deprecated one Safari needed until 14. Support both — a thrown TypeError
    // here would take the whole screen down.
    queries.forEach(q => {
      if (typeof q.addEventListener === 'function') q.addEventListener('change', sync);
      else if (typeof q.addListener === 'function') q.addListener(sync);
    });

    sync();   // the width may have moved between first render and this effect

    return () => {
      window.removeEventListener('resize', sync);
      queries.forEach(q => {
        if (typeof q.removeEventListener === 'function') q.removeEventListener('change', sync);
        else if (typeof q.removeListener === 'function') q.removeListener(sync);
      });
    };
  }, []);

  const tier = TIER_ORDER.includes(forcedTier) ? forcedTier : tierFor(state.width);
  return {
    tier,
    width: state.width,
    coarse: state.coarse,
    hover: state.hover,
    phoneLayout: usesPhoneLayout(tier),
    atLeast: floor => tierAtLeast(tier, floor),
  };
}

// D6 — THE CONTROL FLOOR COMES FROM THE POINTER, NOT THE WIDTH.
//
// Coarse: anything that COMMITS or DESTROYS is 48; nothing interactive below 40.
// Fine: default 32, and nothing below 24.
//
// EXPORTED NOW, ADOPTED AFTER SESSION 80. Control heights are the geometry half
// of #417's adoption and are deliberately carved out behind the Dallas test —
// raising `.btn` app-wide moves layout on every screen, and doing that DURING the
// live-testing window changes the app under the people testing it. The values
// live here so the two halves cannot drift apart in the meantime.
export const CONTROL = Object.freeze({
  coarse: { commit: 48, min: 40 },
  fine: { commit: 32, min: 24 },
});

export function controlHeight(coarse, kind = 'min') {
  const set = coarse ? CONTROL.coarse : CONTROL.fine;
  return kind === 'commit' ? set.commit : set.min;
}
